import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { CountdownTimer } from '../components/CountdownTimer';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChevronLeft, ChevronRight, Gavel, User, Clock, ShoppingCart, Trash2, Heart } from 'lucide-react';
import { toast } from 'sonner';
import api, { getImageUrl } from '../utils/api';

export const AuctionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [watchlistLoading, setWatchlistLoading] = useState(false);

  useEffect(() => {
    loadAuction();
    loadBids();
    if (user) {
      checkWatchlistStatus();
    }
    
    // Poll for updates every 5 seconds
    const interval = setInterval(() => {
      loadAuction();
      loadBids();
    }, 5000);

    return () => clearInterval(interval);
  }, [id, user]);

  const checkWatchlistStatus = async () => {
    try {
      const response = await api.get(`/user/watchlist/check/${id}`);
      setInWatchlist(response.data.in_watchlist);
    } catch (error) {
      // Ignore errors
    }
  };

  const handleWatchlistToggle = async () => {
    if (!user) {
      toast.error('Please log in to use watchlist');
      navigate('/login');
      return;
    }

    setWatchlistLoading(true);
    try {
      if (inWatchlist) {
        await api.delete(`/user/watchlist/${id}`);
        setInWatchlist(false);
        toast.success('Removed from watchlist');
      } else {
        await api.post(`/user/watchlist/${id}`);
        setInWatchlist(true);
        toast.success('Added to watchlist');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update watchlist');
    } finally {
      setWatchlistLoading(false);
    }
  };

  const loadAuction = async () => {
    try {
      const response = await api.get(`/auctions/${id}`);
      setAuction(response.data);
      // Set suggested bid amount
      if (!bidAmount) {
        setBidAmount((response.data.current_price + 1).toFixed(2));
      }
    } catch (error) {
      console.error('Failed to load auction:', error);
      toast.error('Auction not found');
      navigate('/browse');
    } finally {
      setLoading(false);
    }
  };

  const loadBids = async () => {
    try {
      const response = await api.get(`/bids/${id}`);
      setBids(response.data);
    } catch (error) {
      console.error('Failed to load bids:', error);
    }
  };

  const handlePlaceBid = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please log in to place a bid');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/bids', {
        auction_id: id,
        amount: parseFloat(bidAmount),
      });
      toast.success('Bid placed successfully!');
      loadAuction();
      loadBids();
      // Update suggested bid
      setBidAmount((parseFloat(bidAmount) + 1).toFixed(2));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place bid');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBuyNow = async () => {
    if (!user) {
      toast.error('Please log in to purchase');
      navigate('/login');
      return;
    }

    try {
      const response = await api.post('/payments/checkout', {
        auction_id: id,
        payment_type: 'buy_now',
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate checkout');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this auction? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/auctions/${id}`);
      toast.success('Auction deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete auction');
    }
  };

  const nextImage = () => {
    if (auction?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % auction.images.length);
    }
  };

  const prevImage = () => {
    if (auction?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + auction.images.length) % auction.images.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-lg font-mono text-muted-foreground">Loading auction...</div>
        </div>
      </div>
    );
  }

  if (!auction) return null;

  const isAdmin = user?.is_admin || user?.email === 'brorbyj@gmail.com';
  const isActive = auction.status === 'active';
  const hasBuyNow = auction.buy_now_price && auction.buy_now_price > 0;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-8 rounded-sm"
          data-testid="back-button"
        >
          <ChevronLeft className="h-4 w-4 mr-1" strokeWidth={1.5} />
          Back
        </Button>

        {/* Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Images */}
          <div>
            <div className="relative aspect-[4/3] max-h-[400px] bg-secondary border border-border mb-4">
              {auction.images && auction.images.length > 0 ? (
                <>
                  <img
                    src={getImageUrl(auction.images[currentImageIndex])}
                    alt={auction.title}
                    className="w-full h-full object-contain"
                  />
                  {auction.images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 transition-colors"
                        data-testid="prev-image-button"
                      >
                        <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 transition-colors"
                        data-testid="next-image-button"
                      >
                        <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  No Image
                </div>
              )}
              {/* Status Badge */}
              {auction.status === 'sold' && (
                <div className="absolute top-4 right-4 bg-destructive text-white px-4 py-2 text-sm font-bold font-mono rounded-sm" data-testid="sold-badge">
                  SOLD
                </div>
              )}
              {isActive && (
                <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-3 py-1.5 text-sm font-bold font-mono rounded-sm animate-pulse" data-testid="live-badge">
                  LIVE
                </div>
              )}
            </div>

            {/* Thumbnail Strip */}
            {auction.images && auction.images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {auction.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-16 h-16 flex-shrink-0 border-2 transition-colors ${
                      idx === currentImageIndex ? 'border-primary' : 'border-border'
                    }`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img src={getImageUrl(img)} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Details */}
          <div>
            <div className="flex items-start justify-between mb-2">
              <p className="text-xs font-mono tracking-wider uppercase text-muted-foreground">
                {auction.category}
              </p>
              {/* Watchlist Button */}
              {!isAdmin && (
                <Button
                  onClick={handleWatchlistToggle}
                  disabled={watchlistLoading}
                  variant="outline"
                  className={`h-9 px-3 rounded-sm ${inWatchlist ? 'border-red-500 text-red-500 hover:bg-red-50' : ''}`}
                  data-testid="watchlist-detail-button"
                >
                  <Heart className={`h-4 w-4 mr-2 ${inWatchlist ? 'fill-current' : ''}`} strokeWidth={2} />
                  {inWatchlist ? 'Watching' : 'Watch'}
                </Button>
              )}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tighter font-heading mb-4" data-testid="auction-detail-title">
              {auction.title}
            </h1>

            {/* Seller */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              <User className="h-4 w-4" strokeWidth={1.5} />
              <span>Sold by <span className="font-medium text-foreground">Green Frog Deals</span></span>
            </div>

            {/* Price Box */}
            <div className="border-2 border-border p-6 mb-6">
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-1">Current Bid</p>
                <p className="text-5xl font-bold font-mono tracking-tight tabular-nums" data-testid="auction-current-price">
                  ${auction.current_price.toFixed(2)}
                </p>
              </div>
              
              {isActive && (
                <div className="flex items-center gap-2 text-sm mb-4">
                  <Clock className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <CountdownTimer endDate={auction.end_date} />
                  <span className="text-muted-foreground">· {auction.bid_count} bids</span>
                </div>
              )}

              {/* Buy Now Price */}
              {hasBuyNow && isActive && (
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-1">Or Buy It Now</p>
                  <p className="text-2xl font-bold font-mono text-primary tabular-nums" data-testid="auction-buy-now-price">
                    ${auction.buy_now_price.toFixed(2)}
                  </p>
                </div>
              )}
            </div>

            {/* Actions - Show bidding to non-admins */}
            {isActive && !isAdmin && (
              <div className="space-y-4 mb-8">
                {/* Place Bid */}
                <form onSubmit={handlePlaceBid} className="space-y-3">
                  <Label htmlFor="bidAmount" className="text-sm font-medium">
                    Your Bid (USD)
                  </Label>
                  <div className="flex gap-3">
                    <Input
                      id="bidAmount"
                      type="number"
                      step="0.01"
                      min={auction.current_price + 0.01}
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      required
                      className="h-12 rounded-sm font-mono text-lg"
                      data-testid="bid-amount-input"
                    />
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="h-12 px-6 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
                      data-testid="place-bid-button"
                    >
                      <Gavel className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      {submitting ? 'Placing...' : 'Place Bid'}
                    </Button>
                  </div>
                </form>

                {/* Buy Now */}
                {hasBuyNow && (
                  <Button
                    onClick={handleBuyNow}
                    className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-bold tracking-wide uppercase rounded-sm shadow-lg shadow-primary/20"
                    data-testid="buy-now-button"
                  >
                    <ShoppingCart className="h-5 w-5 mr-2" strokeWidth={1.5} />
                    Buy It Now - ${auction.buy_now_price.toFixed(2)}
                  </Button>
                )}
              </div>
            )}

            {/* Admin Controls */}
            {isAdmin && (
              <div className="bg-secondary/50 border border-border p-4 mb-8">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Admin Controls</p>
                  <Button
                    onClick={handleDelete}
                    variant="destructive"
                    className="h-9 px-4 rounded-sm"
                    data-testid="delete-auction-button"
                  >
                    <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Delete Auction
                  </Button>
                </div>
              </div>
            )}

            {/* Description */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold font-heading mb-3">Description</h3>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap" data-testid="auction-description">
                {auction.description}
              </p>
            </div>

            {/* Bid History */}
            <div>
              <h3 className="text-xl font-semibold font-heading mb-4">Bid History</h3>
              {bids.length === 0 ? (
                <p className="text-muted-foreground">No bids yet. Be the first to bid!</p>
              ) : (
                <div className="space-y-2" data-testid="bid-history">
                  {bids.map((bid, index) => (
                    <div
                      key={bid.bid_id}
                      className="flex items-center justify-between p-3 bg-secondary/30 border border-border"
                      data-testid={`bid-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <User className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                        <span className="font-medium">{bid.bidder_name}</span>
                        {index === 0 && (
                          <span className="bg-primary text-primary-foreground px-2 py-0.5 text-xs font-bold font-mono rounded-sm" data-testid="highest-bid-badge">
                            HIGHEST
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold font-mono tabular-nums">${bid.amount.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(bid.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
